import { IonContent } from '@ionic/react';
import { AnimatePresence, motion } from 'motion/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { UiTypography } from '@/components/ui/Typography';
import { S3_BUCKET_URL } from '@/constants';
import { useAccount } from '@/hooks/useAccount';
import { amplitudeModule } from '@/modules/amplitude';
import { OnboardingFooter } from '@/pages/Onboarding/components/Footer';
import { useOnboarding } from '@/pages/Onboarding/context';
import { canLogEvents } from '@/utils/canLogEvents';

import { LayoutDataType } from '../../types';

import {
  AnimatedBottomButtonContainer,
  BlurOverlayStyles,
  ChatContainerStyles,
  ChatMessageStyles,
  GlowOverlayStyles,
  ScreenChatPreviewStyles,
} from './ChatPreview.styles';

const ASSETS_PATH = `${S3_BUCKET_URL}/paywall/forest`;
const LAYOUT_PATH = `${S3_BUCKET_URL}/layout-own-ai-multigender-forest`;

interface ChatMessageProps {
  content: string;
  variant: 'sent' | 'received';
  show?: boolean;
  delay?: number;
}

const ChatMessage: React.FC<ChatMessageProps> = ({
  content,
  variant,
  show = true,
  delay = 0,
}) => {
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          layout
          transition={{
            duration: 0.3,
            delay,
            ease: [0.25, 0.46, 0.45, 0.94],
            layout: { duration: 0.2 },
          }}
        >
          <ChatMessageStyles variant={variant}>
            <UiTypography variant="body-large" component="span">
              {content}
            </UiTypography>
          </ChatMessageStyles>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const ScreenChatPreview: React.FC = () => {
  const { wizard } = useOnboarding();
  const form = useFormContext<LayoutDataType>();
  const { user } = useAccount();

  const gender =
    form.getValues('gender') || user?.onboarding_data.gender || 'female';
  const bodyShape =
    form.getValues('body_shape') ||
    user?.onboarding_data.body_shape ||
    'regular';

  // Animation states
  const [showFirstMessage, setShowFirstMessage] = React.useState(false);
  const [showSecondMessage, setShowSecondMessage] = React.useState(false);
  const [showFooter, setShowFooter] = React.useState(false);

  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'ChatPreview',
      });
    }
  }, []);

  // Show first message
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setShowFirstMessage(true);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // Show second message after first
  React.useEffect(() => {
    if (showFirstMessage) {
      const timer = setTimeout(() => {
        setShowSecondMessage(true);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [showFirstMessage]);

  // Show footer after second message
  React.useEffect(() => {
    if (showSecondMessage) {
      const timer = setTimeout(() => {
        setShowFooter(true);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [showSecondMessage]);

  const handleNextStep = (): void => {
    wizard.nextStep();
  };

  const bgImageSrc = `${ASSETS_PATH}/${gender}/${bodyShape}.png`;

  return (
    <ScreenChatPreviewStyles
      bgImage={<img src={bgImageSrc} alt="Chat Preview" />}
      bgImageMask={false}
      background="dark-solid"
    >
      <BlurOverlayStyles />
      <GlowOverlayStyles
        backgroundSrc={`${LAYOUT_PATH}/chat-preview/glowing.png`}
      />

      <IonContent />

      <ChatContainerStyles>
        <ChatMessage
          content="The wild can be harsh... but not tonight."
          variant="received"
          show={showFirstMessage}
          delay={0}
        />

        <ChatMessage
          content="Here, beside me, you can rest."
          variant="received"
          show={showSecondMessage}
          delay={0.2}
        />
      </ChatContainerStyles>

      <AnimatedBottomButtonContainer show={showFooter}>
        <OnboardingFooter
          onContinue={handleNextStep}
          buttonText="Stay with me"
          variant="with-content"
          contentText="We can stay like this for as long as you need."
        />
      </AnimatedBottomButtonContainer>
    </ScreenChatPreviewStyles>
  );
};
